import { FundingRound, getFundingRoundLeadInvestors, getFundingRoundOtherInvestors } from 'entities-next/fundingRounds'
import { flow, isEmpty } from 'lodash'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { renderArrayLabel } from 'utils-next/arrays'
import { format } from 'utils-next/dates'
import { ExtendableStyles, Testable } from 'utils-next/types'
import { groupCurrency } from 'utils/currency'

import { List, ListItem, Theme, Typography } from '@mui/material'

export type FundingRoundViewProps = ExtendableStyles &
  Testable & {
    data: FundingRound
  }

export function FundingRoundView({ data }: FundingRoundViewProps) {
  const fundingRoundData = useFundingRoundData(data)
  const { classes } = useStyles()

  return (
    <List dense>
      {fundingRoundData.map(({ key, label, data }) => (
        <ListItem key={key} className={classes.fundingRoundData}>
          <Typography className={classes.fundingRoundLabel}>{label}</Typography>
          <Typography>{data}</Typography>
        </ListItem>
      ))}
    </List>
  )
}

function useFundingRoundData(fundingRound: FundingRound): FundingRoundData[] {
  const { id: fundingRoundId, roundType, announcedOn, roundSize, targetMoney, valuationPreMoney } = fundingRound
  const leadInvestors = getFundingRoundLeadInvestors(fundingRound)
  const otherInvestors = getFundingRoundOtherInvestors(fundingRound)

  const fundingRoundData: FundingRoundData[] = []
  if (announcedOn) {
    fundingRoundData.push({
      label: 'Announced On',
      key: `funding-round-${fundingRoundId}-announcedOn`,
      data: format(new Date(announcedOn), 'yyyy-MM-dd'),
    })
  } else {
    fundingRoundData.push({
      label: 'Currently raising',
      key: `funding-round-${fundingRoundId}-announcedOn`,
      data: '',
    })
  }
  if (roundType) {
    fundingRoundData.push({
      label: 'Round type',
      key: `funding-round-${fundingRoundId}-roundType`,
      data: roundType,
    })
  }
  if (roundSize) {
    fundingRoundData.push({
      label: 'Round size',
      key: `funding-round-${fundingRoundId}-moneyRaised`,
      data: flow(groupCurrency, renderArrayLabel)(roundSize),
    })
  }
  if (targetMoney) {
    fundingRoundData.push({
      label: 'Target money',
      key: `funding-round-${fundingRoundId}-targetMoney`,
      data: flow(groupCurrency, renderArrayLabel)(targetMoney),
    })
  }
  if (valuationPreMoney) {
    fundingRoundData.push({
      label: 'Valuation pre-money',
      key: `funding-round-${fundingRoundId}-valuationPreMoney`,
      data: flow(groupCurrency, renderArrayLabel)(valuationPreMoney),
    })
  }
  if (!isEmpty(leadInvestors)) {
    fundingRoundData.push({
      label: 'Lead investors',
      key: `funding-round-${fundingRoundId}-leadInvestor`,
      data: renderArrayLabel(leadInvestors),
    })
  }
  if (!isEmpty(otherInvestors)) {
    fundingRoundData.push({
      label: 'Investors',
      key: `funding-round-${fundingRoundId}-otherInvestor`,
      data: renderArrayLabel(otherInvestors),
    })
  }
  return fundingRoundData
}

type FundingRoundData = {
  label: string
  key: string
  data: string
}

const useStyles = makeStyles()(({ spacing, typography }: Theme) => ({
  fundingRound: {
    display: 'flex',
    flexDirection: 'column',
  },
  fundingRoundData: {
    display: 'flex',
  },
  fundingRoundLabel: {
    flex: '0 0 auto',
    fontWeight: typography.fontWeightBold,
    marginRight: spacing(),
  },
  sectionContent: {
    padding: spacing(1, 0),
  },
}))
